import React, { Component, Fragment } from "react";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { Button as PrimeButton } from "primereact/button";
import PageTitle from "../../Layout/AppMain/PageTitle";
import { AppCard, FormField } from "../../components/ui";
import { Toast } from "primereact/toast";
import _ from "lodash";
import { utils } from "../../utils/utils";
import { CardInfo, CardStatus, getCardDesignLabel } from "../../models/IssuingClientsModel";
import ExtendedInputText from "../../components/ExtendedInputText";
import { LoadingDots } from "../../components/ui";
import ClientCardEventsComponent from "./CardEvents";
import { issuingApiService } from "../../services/api/Issuing/IssuingApiService";
import { confirmDialog } from "primereact/confirmdialog";

import "./issuing.scss";
import { CreditCard } from "lucide-react";
interface ClientDetailsState {
    isLoading: boolean;
    card?: CardInfo | null;

    cardId?: number;
    individualId?: number;

}

export default class ICardDetailsComponent extends Component<Record<string, never>, ClientDetailsState> {
    toast: Toast | null = null;

    validationErrors: Map<string, boolean> = new Map<string, boolean>();

    constructor(props: Record<string, never>) {
        super(props);
        const parts = window.location.pathname.split("/").filter(Boolean);

        const cardId = Number(parts[parts.length - 2]);
        const individualId = Number(parts[parts.length - 1]);

        this.state = {
            isLoading: false,
            cardId,
            individualId,
            card: null
        };
    }
    async getCardDetails() {
        this.setState({ isLoading: true })

        const res = await issuingApiService.getCardDetails({
            IndividualId: this.state.individualId!,
            CardId: this.state.cardId!,
        });
        if (res.success) {
            this.setState({
                card: res.data,
                isLoading: false
            })
        }
    }
    componentDidMount(): void {
        this.getCardDetails();
    }

    getAvailableActions = (status?: CardStatus) => {
        switch (status) {
            case CardStatus.ProducedAndInactive: // 9
                return [{ label: "Activate Card", next: CardStatus.Active }]; // 0

            case CardStatus.Active: // 0
                return [
                    { label: "Report Lost", next: CardStatus.Lost },         // 1
                    { label: "Report Stolen", next: CardStatus.Stolen },     // 2
                    { label: "Destroy Card", next: CardStatus.Destroyed },   // 3
                    { label: "Block by Partner", next: CardStatus.BlockedByPartner },           // 8
                ];

            case CardStatus.Lost: //1
            case CardStatus.Stolen: //2
            case CardStatus.BlockedFraudSuspected: //7
                return [{ label: "Destroy Card", next: CardStatus.Destroyed }]; // 3



            case CardStatus.BlockedByPartner: //8
                return [
                    { label: "Unblock Card", next: CardStatus.Active },    // 0
                    { label: "Report Lost", next: CardStatus.Lost },       // 1
                    { label: "Report Stolen", next: CardStatus.Stolen },   // 2
                    { label: "Destroy Card", next: CardStatus.Destroyed }, // 3
                ];

            default:
                return [];
        }
    };


    // ==============================
    // UNBLOCK PIN
    // ==============================

    unblockPin = async () => {
        if (!this.state.card) return;

        confirmDialog({
            message: "Are you sure you want to unblock the PIN?",
            header: "Confirmation",
            icon: "pi pi-exclamation-triangle",
            accept: async () => {
                this.setState({ isLoading: true });

                const parts = window.location.pathname.split("/").filter(Boolean);
                const cardId = Number(parts[parts.length - 2]);
                const individualId = Number(parts[parts.length - 1]);

                try {
                    const res = await issuingApiService.updatePinStatus({
                        cardId,
                        individualId,
                    });

                    if (res.success) {
                        this.toast?.show({
                            severity: "success",
                            summary: "PIN Unblocked",
                            detail: "The PIN has been successfully unblocked.",
                            life: 5000,
                        });
                    } else {
                        this.toast?.show({
                            severity: "error",
                            summary: "Error",
                            detail: res.message ?? "Failed to unblock PIN",
                            life: 5000,
                        });
                    }
                } catch (err) {
                    this.toast?.show({
                        severity: "error",
                        summary: "Error",
                        detail: "Something went wrong while unblocking PIN",
                        life: 5000,
                    });
                } finally {
                    this.setState({ isLoading: false });
                }
            },
            reject: () => {
                // optional: do nothing
            },
        });
    };

    updateCardStatus = async (nextStatus: CardStatus) => {
        if (!this.state.card) return;

        this.setState({ isLoading: true });
        const parts = window.location.pathname.split("/").filter(Boolean);

        const cardId = Number(parts[parts.length - 2]);
        const individualId = Number(parts[parts.length - 1]);
        const res = await issuingApiService.updateCardStatus({
            cardId,
            individualId,
            cardStatus: nextStatus,
        });

        if (res.success) {
            this.toast?.show({
                severity: "success",
                summary: "Status Updated",
                detail: `Card status changed to ${nextStatus}`,
            });

            this.setState({
                card: {
                    ...this.state.card,
                    cardStatus: nextStatus,
                },
                isLoading: false,
            });
        } else {
            this.toast?.show({
                severity: "error",
                summary: "Error",
                detail: res.message ?? "Failed to update card status",
            });

            this.setState({ isLoading: false });
        }
    };

    updateValue = (fieldName: string, value: string) => {
        const merchantDetails = this.state.card as any;

        merchantDetails[fieldName] = value;

        this.setState({ card: merchantDetails });
    };

    renderStatusActionsSection = () => {
        const status = this.state.card?.cardStatus;
        if (!status) return null;

        const actions = this.getAvailableActions(status);
        if (!actions.length) return null;

        const destructiveStatuses = [
            CardStatus.Destroyed,
            CardStatus.Lost,
            CardStatus.Stolen,
        ];

        return (
            <div className="p-3 mt-2 border rounded bg-neutral-50 dark:bg-neutral-800">
                <label className="block text-sm font-medium text-neutral-500 mb-2">Available Actions</label>

                <div className="flex flex-wrap gap-2">
                    {actions.map((action) => {
                        const isDanger = destructiveStatuses.includes(action.next);

                        return (
                            <PrimeButton
                                key={action.label}
                                size="small"
                                severity={isDanger ? "danger" : undefined}
                                outlined={!isDanger}
                                label={action.label}
                                onClick={() => this.confirmStatusChange(action.next)}
                            />
                        );
                    })}
                </div>
            </div>
        );
    };

    confirmStatusChange = (nextStatus: CardStatus) => {
        if (!this.state.card) return;

        confirmDialog({
            message: `Are you sure you want to change card status to "${nextStatus}"?`,
            header: "Confirmation",
            icon: "pi pi-exclamation-triangle",
            accept: () => {
                this.updateCardStatus(nextStatus);
            },
            reject: () => {
                // optional: do nothing
            },
        });
    };
    renderCardBody = () => {
        if (this.state.isLoading || !this.state.card) {
            return (
                <AppCard>
                    <div className="flex items-center justify-center" style={{ minHeight: "500px" }}>
                        <LoadingDots />
                    </div>
                </AppCard>
            );
        }

        return (
            <AppCard>
                <AppCard.Header title="Card Details" />
                <AppCard.Body>
                    <div className="max-w-3xl">
                        <FormField.Grid cols={2}>
                            <FormField label="Pan" required>
                                <ExtendedInputText
                                    name="flexCubeBranchCode"
                                    value={this.state.card?.maskedPan}
                                    required={true}
                                    readOnly={true}
                                    onChange={(value) => this.updateValue("flexCubeBranchCode", value)}
                                    onValidStatusChanged={(value: boolean) => this.validationErrors.set("flexCubeBranchCode", value)}
                                />
                            </FormField>
                            <FormField label="Expiry" required>
                                <ExtendedInputText
                                    type="email"
                                    value={this.state.card?.expiryDate}
                                    onChange={(value) => this.updateValue("email", value)}
                                    readOnly={true}
                                    name="Email"
                                    required={true}
                                    onValidStatusChanged={(value: boolean) => this.validationErrors.set("email", value)}
                                />
                            </FormField>
                            <FormField label="Design">
                                <ExtendedInputText
                                    name="cardDesign"
                                    value={this.state.card ? getCardDesignLabel(this.state.card.cardDesign) : ""}
                                    readOnly={true}
                                />
                            </FormField>
                        </FormField.Grid>

                        <div className="mt-5">
                            <FormField label="Current Status">
                                <div className="flex items-center mb-3 gap-3">
                                    <span
                                        className={`badge badge-pill px-3 py-2 ${utils.getStatusBadgeClass(this.state.card?.cardStatus)}`}
                                        style={{ fontSize: "0.95rem" }}
                                    >
                                        {this.state.card?.cardStatus}
                                    </span>

                                    <PrimeButton size="small" severity="warning" label="Unblock PIN" onClick={this.unblockPin} />
                                </div>

                                {this.renderStatusActionsSection()}
                            </FormField>
                        </div>
                    </div>
                </AppCard.Body>
            </AppCard>
        );
    };

    render() {
        return (
            <Fragment>
                <TransitionGroup component="div" appear={true} exit={false} enter={false}>
                    <CSSTransition classNames="TabsAnimation" timeout={1500}>
                        <PageTitle heading="Card Details" icon={CreditCard} />
                    </CSSTransition>
                    <CSSTransition classNames="TabsAnimation" timeout={1500}>
                        <Fragment>
                            <Toast ref={(el) => (this.toast = el)} position={"bottom-center"} baseZIndex={99999}></Toast>
                            <div className="flex flex-col gap-4">
                                {this.renderCardBody()}
                                <ClientCardEventsComponent
                                    cardId={this.state.cardId}
                                    IndividualId={this.state.individualId}
                                />

                            </div>
                        </Fragment>
                    </CSSTransition>
                </TransitionGroup>
            </Fragment>
        );
    }
}
